import { ForwardedRef, forwardRef, Fragment, useCallback, useEffect, useState } from 'react';
import { IconChevron } from '@components/icons/src/icon-chevron';
import {
  Listbox as HeadlessListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import classNames from 'classnames';

import { Option, Props } from './listbox.types';

// @see https://headlessui.dev/react/listbox

export const Listbox = forwardRef(
  (
    { options, value, onChange }: Props,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: ForwardedRef<unknown>
  ) => {
    const getOptionFromValue = useCallback(
      (val: number | string) => {
        return options.find((option) => option.id === val);
      },
      [options]
    );

    const [selectedOption, setSelectedOption] = useState(
      value && getOptionFromValue(value) ? getOptionFromValue(value) : options[0]
    );

    const handleChange = (newValue: Option) => {
      const option = getOptionFromValue(newValue.id);
      option && setSelectedOption(option);
      onChange?.(newValue.id);
    };

    useEffect(() => {
      if (value) {
        const option = getOptionFromValue(value);
        option && setSelectedOption(option);
        onChange?.(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOptionFromValue, value]);

    return (
      <HeadlessListbox value={selectedOption} onChange={handleChange}>
        {({ open }) => (
          <div className='mt-1 relative'>
            <div className='flex justify-end'>
              <ListboxButton className='inline-flex items-center text-primary cursor-pointer focus:outline-none'>
                <span className='text-subtitle-s'>{selectedOption?.value}</span>
                <span className='block ml-2'>
                  <IconChevron
                    className={classNames('w-3 h-3 -rotate-90 transition-transform', { 'rotate-90': open })}
                  />
                </span>
              </ListboxButton>
            </div>

            <Transition
              show={open}
              as={Fragment}
              enter='transition ease-out duration-200'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition ease-in-out duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              <ListboxOptions
                modal={false}
                static
                anchor={{ to: 'bottom end', gap: 16 }}
                className='flex flex-col w-56 bg-primary shadow-sm rounded-md overflow-auto focus:outline-none text-body-xs dark:shadow-black-1000/30 px-2 py-3 gap-2'>
                {options.map((option) => (
                  <ListboxOption
                    key={option.id}
                    className={({ focus, selected }) =>
                      `${classNames('text-subtitle-s text-primary', {
                        'cursor-pointer': !selected,
                        'fill-secondary': !focus && !selected,
                        'fill-secondary-active': focus || selected,
                      })} px-4 py-2`
                    }
                    value={option}>
                    {option.value}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </HeadlessListbox>
    );
  }
);
